/**
 * Copyright (c) 2021-2024 Kognitos, Inc. All rights reserved.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Department,
  GetProcedureQueryVariables,
  Procedure,
  Worker,
  useSetWorkerArchivedMutation
} from '@/generated/graphql';
import { runSelector } from '@/stores/slices/run';
import { useAppDispatch, useAppSelector, useATQ } from '@/stores/hooks';
import {
  getWorker,
  workerSelector,
  updateWorker
} from '@/stores/slices/worker';
import { RootState } from '@/stores/AppStore';
import { isNil } from 'ramda';
import { useParams } from 'react-router-dom';
import { message } from 'antd';
import { GET_PROCEDURE } from '@/graphql/queries/process';
import { useQuery } from '@apollo/client';
import {
  departmentQuerySelector,
  getDepartment
} from '../stores/slices/department';
import useRunSubscriptions from '../hooks/useRunSubscriptionts';

export interface RunContextType {
  worker: Worker;
  loading: boolean;
  error: any;
  procedure: Procedure | null;
  workerDepartment: Department | undefined;
  toggleArchive: (
    archiveStatus: boolean,
    onSuccess?: (archiveStatus: boolean) => void
  ) => void;
}

export const RunContextV2 = React.createContext<RunContextType>(null!);

const RunProviderSelector = (state: RootState) => {
  const { workerId } = runSelector(state);
  const worker = workerSelector.selectById(state, workerId!)!;
  return {
    workerId,
    worker
  };
};

interface IProps {
  children: React.ReactNode;
  procedureId?: string;
  readOnly?: boolean;
  fetchWorker?: boolean;
}

export function RunProviderV2(props: IProps) {
  const {
    children,
    procedureId: initialProcedureId,
    readOnly = false,
    fetchWorker = true
  } = props;

  const { workerId, worker } = useAppSelector(RunProviderSelector);

  const params = useParams();

  const { knowledgeId, departmentId } = params;
  const procedureId = initialProcedureId || params.procedureId;

  const procedureVariables: GetProcedureQueryVariables = {
    procedureId: procedureId!
  };

  // TODO: Remove knowledgeId if not being used anywhere
  if (knowledgeId && knowledgeId !== 'null') {
    procedureVariables.knowledgeId = knowledgeId;
  } else if (departmentId && departmentId !== 'null') {
    procedureVariables.departmentId = departmentId;
  }

  const [procedure, setProcedure] = useState<Procedure | null>(null);

  useQuery(GET_PROCEDURE, {
    variables: procedureVariables,
    onCompleted: (data) => {
      if (data?.getProcedure) {
        setProcedure(data.getProcedure);
      }
    },
    skip: !procedureId
  });

  const { department: selectedDepartment, departments } = useAppSelector(
    departmentQuerySelector
  );
  const department = departments.find((dep) => dep.id === departmentId);

  const dispatch = useAppDispatch();

  const [setWorkerArchived] = useSetWorkerArchivedMutation();

  useEffect(() => {
    if (departmentId && departmentId !== selectedDepartment?.id) {
      dispatch(getDepartment({ id: departmentId }));
    }
  }, [departmentId, selectedDepartment]);

  const { loading, error } = useATQ(getWorker, {
    variables: { id: workerId },
    wait: !fetchWorker || isNil(workerId)
  });

  useRunSubscriptions({
    workerId,
    onWorkerKnowledgeIdUpdate: (knowledgeId) => {
      dispatch(updateWorker({ id: workerId!, changes: { knowledgeId } }));
    },
    skip: readOnly
  });

  const toggleArchive = useCallback(
    async (
      archiveStatus: boolean,
      onSuccess?: (archiveStatus: boolean) => void
    ) => {
      try {
        await setWorkerArchived({
          variables: {
            input: {
              id: worker?.id,
              isArchived: archiveStatus
            }
          }
        });

        if (archiveStatus) {
          message.success(`Run archived successfully`);
        } else {
          message.success(`Run un-archived successfully`);
        }
        if (onSuccess) {
          onSuccess(archiveStatus);
        }
      } catch (e) {
        message.error(
          `Couldn't ${
            archiveStatus ? 'archive' : 'un-archive'
          } run, please try again`
        );
      }
    },
    [worker?.id, setWorkerArchived]
  );

  // eslint-disable-next-line react/jsx-no-constructed-context-values
  const value: RunContextType = {
    worker,
    loading,
    error,
    procedure,
    workerDepartment: department,
    toggleArchive
  };

  return (
    <RunContextV2.Provider value={value}>{children}</RunContextV2.Provider>
  );
}

export function useRunCtxV2() {
  return React.useContext(RunContextV2);
}
